import { KeyboardEvent, PointerEvent, useRef, useState } from 'react'
import {
  clampTrim,
  cutForWords,
  fillerCuts,
  formatTime,
  isCut,
  keptSeconds,
  mergeCuts,
  snapToWord,
  type Cut,
  type Range,
  type Word,
} from './clipUtils'

interface TrimEditorProps {
  range: Range
  /** The clip as suggested, for "Reset" and to centre the timeline. */
  suggested: Range
  duration: number
  onChange: (range: Range) => void
  onReset: () => void
  onPlay: () => void
  saving?: boolean
  /** The transcript's timed words around the clip, for snapping and cutting; null while loading. */
  words?: Word[] | null
}

// The timeline shows the clip with this much video either side.
const MARGIN_SECONDS = 30

/**
 * Start and end handles on a timeline, nudges of 1 s and 0.1 s, "Play
 * trimmed" and reset. Every change is kept inside the video (clampTrim).
 * Dragged handles snap to the nearest word edge, so a clip doesn't start or end
 * mid-word (nudges stay exact). Below, the clip's words: pick a run of them to
 * cut it out, or cut every "um" and "uh" at once.
 */
const TrimEditor = ({ range, suggested, duration, onChange, onReset, onPlay, saving, words }: TrimEditorProps) => {
  const bar = useRef<HTMLDivElement>(null)
  const [snap, setSnap] = useState(true)
  const [picked, setPicked] = useState<{ from: number; to: number } | null>(null)
  const cuts = range.cuts || []
  const inClip = (words || []).filter((w) => (w.start + w.end) / 2 >= range.start && (w.start + w.end) / 2 <= range.end)
  const fillers = fillerCuts(inClip, cuts)
  const from = Math.max(0, Math.min(range.start, suggested.start) - MARGIN_SECONDS)
  const to = Math.min(duration, Math.max(range.end, suggested.end) + MARGIN_SECONDS)
  const span = Math.max(to - from, 0.001)
  const pct = (t: number) => `${((t - from) / span) * 100}%`

  const move = (edge: 'start' | 'end', value: number) => onChange(clampTrim({ ...range, [edge]: value }, duration, edge))

  const setCuts = (next: Cut[]) => {
    setPicked(null)
    onChange({ ...range, cuts: mergeCuts(range, next) })
  }

  const drag = (edge: 'start' | 'end') => (event: PointerEvent<HTMLDivElement>) => {
    const rect = bar.current?.getBoundingClientRect()
    if (!rect || !rect.width) return
    event.currentTarget.setPointerCapture?.(event.pointerId)
    const follow = (x: number) => {
      const t = from + ((x - rect.left) / rect.width) * span
      move(edge, snap && words ? snapToWord(t, words, edge) : t)
    }
    const onMove = (e: globalThis.PointerEvent) => follow(e.clientX)
    const onUp = () => {
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
    }
    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onUp)
  }

  const keys = (edge: 'start' | 'end') => (event: KeyboardEvent<HTMLDivElement>) => {
    const step = event.shiftKey ? 1 : 0.1
    const delta = { ArrowLeft: -step, ArrowDown: -step, ArrowRight: step, ArrowUp: step }[event.key]
    if (delta === undefined) return
    event.preventDefault()
    move(edge, range[edge] + delta)
  }

  const handle = (edge: 'start' | 'end') => (
    <div
      role="slider"
      tabIndex={0}
      aria-label={edge === 'start' ? 'Trim start' : 'Trim end'}
      aria-valuemin={0}
      aria-valuemax={Math.round(duration * 10) / 10}
      aria-valuenow={range[edge]}
      aria-valuetext={formatTime(range[edge])}
      onPointerDown={drag(edge)}
      onKeyDown={keys(edge)}
      className="absolute top-0 h-full w-3 -translate-x-1/2 cursor-ew-resize rounded bg-primary-600 focus:outline-none focus:ring-2 focus:ring-primary-300"
      style={{ left: pct(range[edge]) }}
    />
  )

  const nudges = (edge: 'start' | 'end') => (
    <div className="flex items-center gap-1" role="group" aria-label={edge === 'start' ? 'Nudge start' : 'Nudge end'}>
      {[-1, -0.1, 0.1, 1].map((delta) => (
        <button
          key={delta}
          type="button"
          onClick={() => move(edge, range[edge] + delta)}
          className="rounded border border-gray-300 px-2 py-0.5 text-xs text-gray-700 hover:bg-gray-100"
          aria-label={`${edge === 'start' ? 'Start' : 'End'} ${delta > 0 ? '+' : '−'}${Math.abs(delta)} s`}
        >
          {delta > 0 ? '+' : '−'}
          {Math.abs(delta)}s
        </button>
      ))}
    </div>
  )

  return (
    <div className="mt-3 rounded-md border border-gray-200 bg-gray-50 p-3" data-testid="trim-editor">
      <div className="mb-2 flex justify-between text-xs text-gray-500">
        <span>{formatTime(from)}</span>
        <span>
          {formatTime(range.start)} – {formatTime(range.end)} · {(range.end - range.start).toFixed(1)} s
          {cuts.length > 0 && ` · ${keptSeconds(range).toFixed(1)} s after ${cuts.length} ${cuts.length === 1 ? 'cut' : 'cuts'}`}
          {saving ? ' · saving…' : ''}
        </span>
        <span>{formatTime(to)}</span>
      </div>
      <div ref={bar} className="relative h-8 rounded bg-gray-200">
        <div
          className="absolute top-0 h-full bg-primary-100"
          style={{ left: pct(range.start), width: `calc(${pct(range.end)} - ${pct(range.start)})` }}
        />
        {cuts.map((c) => (
          <div
            key={`${c.start}-${c.end}`}
            data-testid="cut-band"
            className="absolute top-0 h-full bg-danger-500/40"
            style={{ left: pct(c.start), width: `calc(${pct(c.end)} - ${pct(c.start)})` }}
          />
        ))}
        {handle('start')}
        {handle('end')}
      </div>
      <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-xs text-gray-600">
          Start {nudges('start')}
        </div>
        <div className="flex items-center gap-2 text-xs text-gray-600">
          End {nudges('end')}
        </div>
      </div>
      <label className="mt-2 flex items-center gap-2 text-xs text-gray-600">
        <input type="checkbox" className="h-3.5 w-3.5" checked={snap} onChange={(e) => setSnap(e.target.checked)} />
        Snap handles to words
      </label>

      {words && inClip.length > 0 && (
        <div className="mt-3 space-y-2" data-testid="cut-words">
          <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-gray-600">
            <span>Tap a word, then another, to pick the words between. Cut words are struck out; tap one to bring it back.</span>
          </div>
          <p className="max-h-40 overflow-y-auto rounded border border-gray-200 bg-white p-2 text-sm leading-7" aria-label="Clip words">
            {inClip.map((w, i) => {
              const cut = isCut(w, cuts)
              const sel = picked && i >= Math.min(picked.from, picked.to) && i <= Math.max(picked.from, picked.to)
              return (
                <button
                  key={`${w.start}-${i}`}
                  type="button"
                  aria-pressed={!!sel}
                  title={formatTime(w.start)}
                  onClick={() => {
                    if (cut) {
                      setCuts(cuts.filter((c) => !isCut(w, [c])))
                    } else if (picked && picked.from === picked.to) {
                      setPicked({ from: picked.from, to: i })
                    } else {
                      setPicked({ from: i, to: i })
                    }
                  }}
                  className={`mr-1 rounded px-0.5 ${
                    cut ? 'text-gray-400 line-through' : sel ? 'bg-danger-50 text-danger-700' : w.filler ? 'text-amber-700' : 'text-gray-800'
                  } hover:bg-gray-100`}
                >
                  {w.word}
                </button>
              )
            })}
          </p>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              disabled={!picked}
              onClick={() =>
                picked && setCuts([...cuts, cutForWords(inClip, Math.min(picked.from, picked.to), Math.max(picked.from, picked.to))])
              }
              className="rounded-md bg-danger-500 px-2 py-1 text-xs font-medium text-white hover:bg-danger-700 disabled:opacity-50"
            >
              Cut picked words
            </button>
            {fillers.length > 0 && (
              <button
                type="button"
                onClick={() => setCuts([...cuts, ...fillers])}
                className="rounded-md border border-gray-300 px-2 py-1 text-xs text-gray-700 hover:bg-gray-100"
              >
                Cut {fillers.length} {fillers.length === 1 ? 'filler word' : 'filler words'} (um, uh)
              </button>
            )}
            {cuts.length > 0 && (
              <button
                type="button"
                onClick={() => setCuts([])}
                className="rounded-md border border-gray-300 px-2 py-1 text-xs text-gray-700 hover:bg-gray-100"
              >
                Undo all cuts
              </button>
            )}
          </div>
        </div>
      )}

      <div className="mt-3 flex gap-2">
        <button
          type="button"
          onClick={onPlay}
          className="rounded-md bg-primary-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-primary-700"
        >
          Play trimmed
        </button>
        <button
          type="button"
          onClick={onReset}
          disabled={range.start === suggested.start && range.end === suggested.end && cuts.length === 0}
          className="rounded-md border border-gray-300 px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-100 disabled:opacity-50"
        >
          Reset to suggested
        </button>
      </div>
    </div>
  )
}

export default TrimEditor
